/**
 * Text for pdf-lib's standard Helvetica, which uses WinAnsi (Windows-1252).
 * En dashes, bullets, quotes and Danish letters are in that set. Treating
 * every code point above 255 as missing turned them into "?" in the report.
 */

const WIN_ANSI_EXTRA = new Set<number>([
  0x0152, 0x0153, 0x0160, 0x0161, 0x0178, 0x017d, 0x017e, 0x0192, 0x02c6, 0x02dc, 0x2013, 0x2014,
  0x2018, 0x2019, 0x201a, 0x201c, 0x201d, 0x201e, 0x2020, 0x2021, 0x2022, 0x2026, 0x2030, 0x2039,
  0x203a, 0x20ac, 0x2122,
]);

/** Typographic lookalikes that Helvetica does not encode, drawn as the nearest glyph. */
const LOOKALIKE = new Map<number, string>([
  [0x2010, '-'],
  [0x2011, '-'],
  [0x2012, '\u2013'],
  [0x2015, '\u2014'],
  [0x2e3a, '\u2014'],
  [0x2e3b, '\u2014'],
  [0x2212, '-'],
  [0xfe58, '\u2014'],
  [0xfe63, '-'],
  [0xff0d, '-'],
  [0x2024, '.'],
  [0x2025, '..'],
  [0x2027, '\u00b7'],
  [0x2043, '\u2022'],
  [0x2218, '\u00b7'],
  [0x2219, '\u2022'],
  [0x22c5, '\u00b7'],
  [0x25aa, '\u2022'],
  [0x25ab, '\u2022'],
  [0x25cb, '\u2022'],
  [0x25cf, '\u2022'],
  [0x25e6, '\u2022'],
  [0x2023, '\u2022'],
  [0x204c, '\u2022'],
  [0x204d, '\u2022'],
  [0x2981, '\u2022'],
  [0x30fb, '\u00b7'],
  [0xff65, '\u00b7'],
  [0x201b, '\u2018'],
  [0x201f, '\u201c'],
  [0x2032, "'"],
  [0x2033, '"'],
  [0x2035, "'"],
  [0x2036, '"'],
  [0x02bb, '\u2018'],
  [0x02bc, '\u2019'],
]);

const BYTE_TO_UNICODE: Record<number, string> = {
  0x80: '\u20ac',
  0x82: '\u201a',
  0x83: '\u0192',
  0x84: '\u201e',
  0x85: '\u2026',
  0x86: '\u2020',
  0x87: '\u2021',
  0x88: '\u02c6',
  0x89: '\u2030',
  0x8a: '\u0160',
  0x8b: '\u2039',
  0x8c: '\u0152',
  0x8e: '\u017d',
  0x91: '\u2018',
  0x92: '\u2019',
  0x93: '\u201c',
  0x94: '\u201d',
  0x95: '\u2022',
  0x96: '\u2013',
  0x97: '\u2014',
  0x98: '\u02dc',
  0x99: '\u2122',
  0x9a: '\u0161',
  0x9b: '\u203a',
  0x9c: '\u0153',
  0x9e: '\u017e',
  0x9f: '\u0178',
};

function canEncode(code: number): boolean {
  if (code >= 0x20 && code <= 0x7e) return true;
  if (code >= 0xa0 && code <= 0xff) return true;
  return WIN_ANSI_EXTRA.has(code);
}

function isSpace(code: number): boolean {
  return (
    code === 0x09 ||
    code === 0x0a ||
    code === 0x0b ||
    code === 0x0c ||
    code === 0x0d ||
    code === 0x1680 ||
    (code >= 0x2000 && code <= 0x200a) ||
    code === 0x2028 ||
    code === 0x2029 ||
    code === 0x202f ||
    code === 0x205f ||
    code === 0x3000
  );
}

function shouldDrop(code: number): boolean {
  if (code < 0x20 || code === 0x7f) return true;
  if (code >= 0x80 && code <= 0x9f) return true;
  if (code >= 0x0300 && code <= 0x036f) return true;
  if (code >= 0x200b && code <= 0x200f) return true;
  if (code >= 0x2060 && code <= 0x206f) return true;
  if (code >= 0xfe00 && code <= 0xfe0f) return true;
  if (code === 0xfeff) return true;
  return false;
}

export function toPdfWinAnsi(text: string): string {
  let out = '';
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (canEncode(code)) {
      out += ch;
      continue;
    }
    if (isSpace(code)) {
      out += ' ';
      continue;
    }
    const mapped = LOOKALIKE.get(code);
    if (mapped) {
      out += mapped;
      continue;
    }
    if (shouldDrop(code)) continue;
    out += '?';
  }
  return out;
}

/** Inverse of the bytes pdf-lib writes for a standard font. For tests. */
export function decodePdfWinAnsi(latin1: string): string {
  let out = '';
  for (const ch of latin1) {
    const code = ch.charCodeAt(0);
    out += code >= 0x80 && code <= 0x9f ? (BYTE_TO_UNICODE[code] ?? ch) : ch;
  }
  return out;
}
